/**
 * main.js
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */

// Plugins
import { registerPlugins } from '@/plugins'

// Components
import App from './App.vue'

// Composables
import { createApp } from 'vue'

// Styles
import '@/styles/variables.scss'
import '@/services/GlobalVariables.js'

// Hardware back button handler
import { App as CapacitorApp } from '@capacitor/app'
import { SafeArea } from '@capacitor-community/safe-area'
import router from '@/router'
import store from '@/store'
import { initSentry } from '@/sentry'

// Safe area handling for notches and gesture bars
const setSafeAreaProperties = (insets) => {
  document.documentElement.style.setProperty('--safe-area-inset-top', `${insets.top}px`)
  document.documentElement.style.setProperty('--safe-area-inset-bottom', `${insets.bottom}px`)
  document.documentElement.style.setProperty('--safe-area-inset-left', `${insets.left}px`)
  document.documentElement.style.setProperty('--safe-area-inset-right', `${insets.right}px`)
}

SafeArea.getSafeAreaInsets().then(({ insets }) => {
  setSafeAreaProperties(insets)
})

SafeArea.addListener(({ insets }) => {
  setSafeAreaProperties(insets)
})

// Clean up legacy PWA/service worker artifacts so old caches don't hijack loads
async function clearLegacyServiceWorkers() {
  if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((reg) => reg.unregister()))
    } catch (e) {
      console.warn('Failed to unregister legacy service workers', e)
    }
  }
  if (typeof caches !== 'undefined' && caches.keys) {
    try {
      const keys = await caches.keys()
      const staleKeys = keys.filter((k) => /workbox|precache|pwa/i.test(k))
      await Promise.all(staleKeys.map((k) => caches.delete(k)))
    } catch (e) {
      console.warn('Failed to clear legacy PWA caches', e)
    }
  }
}
/**
 * Android hardware back button handling via Capacitor.
 * Behavior:
 * - If there is navigation history (native or SPA), go back within the router.
 * - If on a designated root route, require a second back press within 1200ms to exit the app.
 * - Otherwise, attempt a router.back() fallback.
 *
 * Adjust ROOT_ROUTES if you change your top-level routes.
 */
const ROOT_ROUTES = ['LoginForm', 'my-requests'] // route names
let __backPressTs = 0

if (CapacitorApp && CapacitorApp.addListener) {
  try {
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      const canRouterGoBack = typeof window !== 'undefined' && window.history && window.history.length > 1

      if (canGoBack || canRouterGoBack) {
        if (router && typeof router.back === 'function') router.back()
        else if (typeof window !== 'undefined') window.history.back()
        return
      }

      const current = router && router.currentRoute ? router.currentRoute : null
      const isRoot = current && current.name ? ROOT_ROUTES.includes(current.name) : false

      if (!isRoot) {
        if (router && typeof router.back === 'function') router.back()
        else if (typeof window !== 'undefined') window.history.back()
        return
      }

      const now = Date.now()
      if (now - __backPressTs < 1200) {
        CapacitorApp.exitApp()
      } else {
        __backPressTs = now
        console.log('Press back again to exit')
      }
    })
  } catch (e) {
    console.warn('Capacitor backButton handler failed to init', e)
  }
}

const app = createApp(App)

registerPlugins(app)

initSentry(app, router)
router.isReady().then(() => app.mount('#app'))

// Hydrate the kazang-api session store from persisted state and start
// the app-wide ticker if any session is still active. Fire-and-forget;
// deposit callers will lazily create a session if none is found.
store.dispatch('kazangSession/bootstrap')

// Fire-and-forget cleanup; safe to run after mount
clearLegacyServiceWorkers()
